// apps/web/src/components/painel/home/regiao-estado.tsx — Fase 17.8, Plano 04 (D-13; UI-SPEC U-26).
// Estados de uma região da home, independentes entre si: carregando (esqueleto com as dimensões finais, sem spinner),
// erro da região (role="alert", nova tentativa só dela), 403 (role="status") e perfil sem indicadores.
import type { ReactElement } from 'react';
import { AlertCircle, Lock, RotateCcw } from 'lucide-react';
import { TIMEZONE_OPERACIONAL } from '../../../lib/dia-operacional';
import { Button } from '../../ui/button';
import { Skeleton } from '../../ui/skeleton';
import { PainelSemIndicadores } from '../painel-sem-indicadores';
import '../../../styles/painel-home.css';

const FORMATO_HORA = new Intl.DateTimeFormat('pt-BR', { timeZone: TIMEZONE_OPERACIONAL, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });

/** HH:mm no fuso operacional (America/Sao_Paulo). */
export function formatarHoraDosDados(data: Date): string {
  return FORMATO_HORA.format(data);
}

export interface RegiaoCarregandoProps {
  /** Id da região (`heroi`, `apoio`, `lista`...): compõe o data-testid. */
  readonly id: string;
  /** Altura final da região em px, para não haver deslocamento de layout quando os dados chegam. */
  readonly altura: number;
}

export function RegiaoCarregando({ id, altura }: RegiaoCarregandoProps): ReactElement {
  return (
    <div data-testid={`painel-regiao-${id}-carregando`} aria-busy="true" className="painel-regiao-carregando">
      <Skeleton className="w-full rounded-2xl" style={{ minHeight: altura }} />
    </div>
  );
}

export interface RegiaoErroProps {
  readonly id: string;
  /** Nome da região na frase de erro (ex.: "o indicador principal"). */
  readonly nome: string;
  readonly onTentarNovamente: () => void;
  /** Horário dos dados que continuam na tela, quando houver dados anteriores. */
  readonly dadosDe?: Date;
}

export function RegiaoErro({ id, nome, onTentarNovamente, dadosDe }: RegiaoErroProps): ReactElement {
  return (
    <div className="flex min-w-0 flex-col gap-2" data-testid={`painel-regiao-${id}-erro`}>
      <div role="alert" className="painel-regiao-erro">
        <p className="flex min-w-0 items-start gap-2 text-[length:var(--font-body)] font-normal leading-normal text-foreground">
          <AlertCircle className="mt-1 size-4 shrink-0 text-destructive" aria-hidden="true" />
          <span className="min-w-0 break-words">{`Não foi possível carregar ${nome}. Verifique a conexão e tente novamente.`}</span>
        </p>
        <Button type="button" variant="outline" size="sm" className="[[data-density=comfortable]_&]:h-11" onClick={onTentarNovamente} data-testid={`painel-regiao-${id}-retry`}>
          <RotateCcw aria-hidden="true" />
          <span>Tentar novamente</span>
        </Button>
      </div>
      {dadosDe !== undefined && (
        <p className="text-[length:var(--font-label)] font-normal leading-[1.4] text-muted-foreground">
          {`Dados de ${formatarHoraDosDados(dadosDe)}; podem não refletir a situação atual.`}
        </p>
      )}
    </div>
  );
}

/** 403 numa aba: sem botão; a pessoa entra de novo para atualizar as permissões. */
export function RegiaoSemAcesso(): ReactElement {
  return (
    <p role="status" data-testid="painel-regiao-sem-acesso" className="flex min-w-0 items-start gap-2 text-[length:var(--font-body)] font-normal leading-normal text-muted-foreground">
      <Lock className="mt-1 size-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 break-words">Seu perfil não tem acesso a esta visão. Se isso mudou agora, entre novamente para atualizar suas permissões.</span>
    </p>
  );
}

export interface SemIndicadoresProps {
  readonly permissoes: readonly string[];
  readonly onAbrirMenu?: () => void;
}

/** Sessão sem nenhuma aba: Alert informativo "Seu perfil ainda não tem indicadores no painel." */
export function SemIndicadores({ permissoes, onAbrirMenu }: SemIndicadoresProps): ReactElement {
  return <PainelSemIndicadores permissoes={permissoes} onAbrirMenu={onAbrirMenu} />;
}
